export interface ServiceLogo {
  src: string
  alt: string
}

export interface ServiceData {
  id: string
  slug: string
  title: string
  accent: string
  iconName: string
  summary: string
  description: string
  whatIs: {
    heading: string
    intro: string
    /** A label, an icon (a lucide name), a title and a line each. The labels,
     *  titles and sixth points are placeholders to edit. */
    points: { label: string; icon: string; title: string; text: string }[]
  }
  capabilities: string[]
  tech: string[]
  metaTitle: string
  metaDescription: string
  ogTitle: string
  ogDescription: string
  logos: ServiceLogo[]
  logosLabel: string
  approach: { title: string; description: string }[]
  platforms?: { name: string; description: string; color: string; points: string[] }[]
  relatedFaqIndices: number[]
}

export const services: ServiceData[] = [
  {
    id: 'laravel',
    slug: 'laravel',
    title: 'Laravel Applications',
    accent: 'red',
    iconName: 'Code2',
    summary: 'Web apps built on Laravel, the PHP framework we\'ve worked in for over a decade. SaaS platforms, enterprise systems, anything with real business logic behind it.',
    description: 'We\'ve built internal tools, booking systems, multi-tenant SaaS platforms and customer-facing portals on Laravel. It gives us Octane when an app needs raw speed, Horizon for managing queues, and Sanctum or Passport for authentication. For most of what comes up after that, the ecosystem already has a well-worn answer.',
    whatIs: {
      heading: 'What is Laravel?',
      intro: 'Laravel has been the most widely used PHP framework for years. Taylor Otwell released it in 2011; today it runs everything from one-page business sites to platforms serving millions of requests a day. Forge, Envoyer and Vapor are all built on it. So is most of what we\'ve shipped over the past decade.',
      points: [
        { label: 'The data', icon: 'Database', title: 'Queries that read like English', text: 'Eloquent, its ORM, lets you write expressive database queries instead of raw SQL.' },
        { label: 'Who gets in', icon: 'KeyRound', title: 'Auth built in', text: 'Authentication, authorisation and API tokens are built in, not bolted on.' },
        { label: 'The slow work', icon: 'Timer', title: 'Queues take the strain', text: 'A queue system takes the slow work off the request: sending email, processing files, calling third-party APIs.' },
        { label: 'Out of the box', icon: 'Package', title: 'First-party packages', text: 'First-party packages cover the common needs, including Cashier for payments, Scout for search and Reverb for real-time.' },
        { label: 'Around it', icon: 'Users', title: 'A huge ecosystem', text: 'Thousands of community packages, and long-term support that\'s genuinely maintained.' },
        { label: 'Years from now', icon: 'ShieldCheck', title: 'Built to last', text: 'Predictable release cycles and clear upgrade paths, so the app you launch today is still easy to move forward in five years.' },
      ],
    },
    capabilities: [
      'Multi-tenant SaaS platforms',
      'REST & GraphQL APIs',
      'Queue-driven background processing',
      'Payment integrations (Stripe, GoCardless)',
      'Role-based access control',
      'Real-time with WebSockets & broadcasting',
      'Scheduled tasks & automation',
      'Third-party API integrations',
    ],
    tech: ['Laravel', 'Octane', 'Horizon', 'Sanctum', 'Cashier', 'Scout'],
    metaTitle: 'Laravel Development: Custom Web Apps & SaaS',
    metaDescription: 'Custom Laravel development in Edinburgh: SaaS platforms, APIs, payments and queue-driven systems. 24 years\' experience, working UK-wide.',
    ogTitle: 'Laravel Application Development | AugmentBLU',
    ogDescription: 'Custom web applications built on Laravel. SaaS platforms, APIs, and enterprise systems.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Discovery', description: 'We start by mapping your business logic in a discovery session, before any code gets written' },
      { title: 'Testing', description: 'Every app ships with a real test suite, written in PHPUnit, Pest and browser tests' },
      { title: 'Architecture', description: 'Anything that doesn\'t need to happen on the spot runs through a queue by default' },
      { title: 'Monitoring', description: 'Monitoring, error tracking and structured logging are there from day one, not added later' },
    ],
    relatedFaqIndices: [0, 1, 2, 5, 6, 8, 12, 15],
  },
  {
    id: 'filament',
    slug: 'filamentphp',
    title: 'FilamentPHP',
    accent: 'amber',
    iconName: 'Layers',
    summary: 'Admin panels, dashboards and internal tools, built fast on Filament without giving up flexibility or polish.',
    description: 'Filament lets us stand up rich admin interfaces in a fraction of the time a from-scratch build would take. We\'ve used it for plain CRUD panels and for sprawling multi-resource dashboards with custom widgets, charts and bulk actions. Because it lives inside your Laravel app, there\'s no separate backend to keep alive.',
    whatIs: {
      heading: 'What is FilamentPHP?',
      intro: 'FilamentPHP is a set of tools for building admin panels, dashboards and CRUD interfaces inside a Laravel app. Rather than hand-build every table, form and filter, you point it at your Eloquent models and it generates a professional admin framework around them, which you\'re then free to customise as far as you like.',
      points: [
        { label: 'From your models', icon: 'Table', title: 'CRUD without the boilerplate', text: 'Generates CRUD interfaces straight from your Laravel models, with no boilerplate to write.' },
        { label: 'On the page', icon: 'Zap', title: 'Live without JavaScript', text: 'Built on Livewire, so you get real-time interactivity without touching JavaScript.' },
        { label: 'For the board', icon: 'ChartColumn', title: 'Dashboards that answer', text: 'Custom widgets, charts and stats for the moments an executive dashboard needs them.' },
        { label: 'The awkward bits', icon: 'Network', title: 'Relationships handled', text: 'Relation managers handle the awkward relationships: orders, invoices, team members.' },
        { label: 'Ready made', icon: 'Puzzle', title: 'Plugins for the usual', text: 'A plugin ecosystem for the usual suspects like audit logging, media libraries and imports.' },
        { label: 'Who sees what', icon: 'Lock', title: 'Roles and permissions', text: 'Access is set per role and per record, so each person sees exactly the screens and data they should.' },
      ],
    },
    capabilities: [
      'Custom admin panels & dashboards',
      'Multi-resource management',
      'Custom widgets & charts',
      'Bulk actions & data exports',
      'Advanced form builders',
      'Notification systems',
      'Multi-tenancy support',
      'Plugin development',
    ],
    tech: ['FilamentPHP', 'Livewire', 'Alpine.js', 'Tailwind CSS'],
    metaTitle: 'FilamentPHP Admin Panels & Dashboards',
    metaDescription: 'FilamentPHP admin panel development: custom dashboards, multi-resource management, bulk actions, and plugin development. Built on Laravel by Edinburgh specialists.',
    ogTitle: 'FilamentPHP Admin Panel Development | AugmentBLU',
    ogDescription: 'Admin panels, dashboards, and internal tools built rapidly with FilamentPHP.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Assessment', description: 'We check whether Filament actually fits before committing to it, because it isn\'t always the right call' },
      { title: 'Conventions', description: 'Custom plugins and widgets follow Filament\'s conventions rather than being hacked on top' },
      { title: 'Usability', description: 'We build for the non-technical people who use it daily, so they don\'t need a developer looking over their shoulder' },
      { title: 'Multi-tenancy', description: 'Multi-tenancy gets configured at the database level, not just in the application layer' },
    ],
    relatedFaqIndices: [16, 17, 18, 1, 8, 4],
  },
  {
    id: 'cms',
    slug: 'cms-platforms',
    title: 'CMS Development',
    accent: 'teal',
    iconName: 'PenTool',
    summary: 'Content-managed sites that hand editors real control and leave developers with clean, maintainable code. No bloat, no awkward trade-offs.',
    description: 'Not every project needs a fully custom application. Sometimes the right answer is a proper CMS your team can publish to without ever touching code. We build those on modern, Laravel-based platforms that stay fast and secure, and that people actually like working in. No WordPress plugin sprawl, no security scares, no performance tax.',
    whatIs: {
      heading: 'Why use a modern CMS?',
      intro: 'The traditional platforms, WordPress and Drupal, carry a lot of baggage: plugin bloat, a constant drip of security patches, and performance that sags as you scale. A modern Laravel-based CMS gives editors everything they need without that legacy weight. Your content team gets a control panel that makes sense; your developers get a real framework underneath.',
      points: [
        { label: 'Day to day', icon: 'PenLine', title: 'Editors in control', text: 'Editors handle pages, content and media themselves, with no developer needed for routine updates.' },
        { label: 'Everywhere', icon: 'Share2', title: 'One source, any frontend', text: 'Headless, API-driven delivery means the same content can feed your site, your apps and whatever frontend comes next.' },
        { label: 'Updates', icon: 'ShieldCheck', title: 'Nothing breaks on update', text: 'Custom functionality is written properly in Laravel, so nothing breaks on the next plugin update.' },
        { label: 'History', icon: 'GitBranch', title: 'Every change tracked', text: 'Content is versioned in Git, so every change is tracked and nothing is one bad edit away from gone.' },
        { label: 'At scale', icon: 'Globe', title: 'Multi-site, multi-language', text: 'Multi-site and multi-language are built in rather than bolted on afterwards.' },
        { label: 'Found online', icon: 'Search', title: 'SEO from the start', text: 'Clean URLs, structured data and fast pages come as standard, not as another plugin to install.' },
      ],
    },
    capabilities: [
      'Flexible content structures & blueprints',
      'Multi-site & multi-language support',
      'Custom fieldtypes & addons',
      'SEO & metadata management',
      'Asset management & image transforms',
      'Form builders & data collection',
      'Git-based content versioning',
      'Headless / API-driven content delivery',
    ],
    tech: ['Statamic', 'Craft CMS', 'OctoberCMS'],
    metaTitle: 'Laravel CMS Development: Statamic & Craft',
    metaDescription: 'CMS development on Statamic, Craft CMS and OctoberCMS: Laravel-powered content management with headless delivery, multi-site and no plugin bloat.',
    ogTitle: 'CMS Development | AugmentBLU',
    ogDescription: 'Modern CMS websites built on Laravel: Statamic, Craft CMS, and OctoberCMS. Fast, secure, and editor-friendly.',
    logosLabel: 'Platforms we build on',
    logos: [
      { src: '/images/partners/statamic.svg', alt: 'Statamic' },
      { src: '/images/partners/craftcms.svg', alt: 'Craft CMS' },
      { src: '/images/cms/october.svg', alt: 'OctoberCMS' },
    ],
    approach: [
      { title: 'Platform Selection', description: 'Statamic is our default recommendation for new builds, flat-file or database-backed depending on the project' },
      { title: 'Content Design', description: 'We shape content structures around how your team actually edits, not the other way round' },
      { title: 'Headless Delivery', description: 'Everything we deploy can serve content over an API, so your frontend options stay open' },
      { title: 'Custom Fieldtypes', description: 'When the built-in field types don\'t match your content, we build the ones that do' },
    ],
    platforms: [
      {
        name: 'Statamic',
        description: 'Our first pick for most new CMS work. It\'s Laravel-native, runs flat-file or database-backed, and the control panel is one editors are happy to spend time in.',
        color: '#FF269E',
        points: [
          'Flat-file or an Eloquent database, chosen per project to suit the content',
          'Templating in Antlers or Blade, both at home in a Laravel codebase',
          'Git-based content versioning out of the box, so any edit can be undone',
          'Multi-site and multi-language built in, rather than added through plugins',
        ],
      },
      {
        name: 'Craft CMS',
        description: 'Strong content modelling for genuinely complex editorial needs. Craft 6 moves it onto Laravel, which makes it a solid bet for content-heavy sites that need real structure.',
        color: '#E5422B',
        points: [
          'Matrix fields give you content modelling that\'s hard to beat',
          'Twig templating, with a mature plugin ecosystem for the common needs',
          'Version 6 moves onto Laravel, so it\'s a future-proof investment',
          'Live Preview and an editorial workflow, so edits are checked first',
        ],
      },
      {
        name: 'OctoberCMS',
        description: 'A lighter Laravel-based CMS for simpler sites that still want proper content management. The codebase is clean, the overhead is low, and developers can extend it without a fight.',
        color: '#DC6C27',
        points: [
          'Built entirely on Laravel, so any Laravel developer knows the patterns',
          'Light and fast, with little overhead on the server or in the admin',
          'A marketplace of plugins for the common features, from forms to SEO',
          'Well suited to brochure sites and small business websites',
        ],
      },
    ],
    relatedFaqIndices: [19, 20, 0, 3, 15, 8],
  },
  {
    id: 'frontend',
    slug: 'vue-nuxt',
    title: 'Vue.js & Nuxt',
    accent: 'green',
    iconName: 'Monitor',
    summary: 'Frontends that feel fast and hold up everywhere, whether that\'s a Laravel-integrated SPA or a standalone Nuxt site.',
    description: 'We build frontends that load quickly and behave the same on every device. For Laravel-backed apps that\'s Vue with Inertia; for standalone sites it\'s Nuxt, with SSR and static generation. Styling runs on Tailwind and ShadCN, real-time comes in over WebSockets when a project calls for it, and the whole thing is type-safe with TypeScript.',
    whatIs: {
      heading: 'What are Vue.js & Nuxt?',
      intro: 'Vue.js is a JavaScript framework for building interactive interfaces. Nuxt sits on top of it and adds the full-stack pieces: server-side rendering, routing, deployment tooling. Between them they\'re what we reach for on every frontend, from a Laravel-integrated SPA to a marketing site like this one.',
      points: [
        { label: 'The core', icon: 'Layers', title: 'Vue components', text: 'Vue.js gives you a reactive component model that\'s lighter and easier to pick up than React, without giving up power.' },
        { label: 'The framework', icon: 'Rocket', title: 'Nuxt on top', text: 'Nuxt layers SSR, static generation, file-based routing and SEO tooling on top of Vue. This site runs on it.' },
        { label: 'With Laravel', icon: 'Link', title: 'Inertia, no API needed', text: 'Inertia.js bridges Laravel and Vue, so you build a SPA without building an API and your controllers serve Vue pages.' },
        { label: 'The look', icon: 'Palette', title: 'Consistent by design', text: 'Tailwind and ShadCN give you utility-first styling and accessible component primitives, so the design stays consistent.' },
        { label: 'The safety net', icon: 'FileCode', title: 'Typed throughout', text: 'TypeScript runs throughout, from your API responses down to your component props.' },
        { label: 'For everyone', icon: 'Accessibility', title: 'Accessible by default', text: 'Keyboard, screen reader and contrast are checked as we build, not patched in after launch.' },
      ],
    },
    capabilities: [
      'Server-side rendering (SSR)',
      'Static site generation (SSG)',
      'Laravel Inertia integration',
      'Real-time interfaces with WebSockets',
      'Component libraries & design systems',
      'Progressive Web Apps (PWA)',
      'Animation & interaction design',
      'Accessibility (WCAG compliance)',
    ],
    tech: ['Vue.js', 'Nuxt', 'Inertia', 'ShadCN', 'TypeScript'],
    metaTitle: 'Vue.js & Nuxt Development: SSR & Inertia',
    metaDescription: 'Vue.js and Nuxt frontend development: server-side rendering, static generation, Laravel Inertia integration, and PWAs. TypeScript-first, Edinburgh-based.',
    ogTitle: 'Vue.js & Nuxt Frontend Development | AugmentBLU',
    ogDescription: 'Dynamic frontends with SSR, real-time features, and tight Laravel integration.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Rendering Strategy', description: 'We pick SSR, SSG or SPA based on what the project actually needs, not what\'s fashionable' },
      { title: 'Inertia Integration', description: 'Inertia is our default for Laravel-backed apps; it takes the whole API layer off the table' },
      { title: 'Component Libraries', description: 'Component libraries are built on ShadCN and Radix, so accessibility comes as standard' },
      { title: 'Type Safety', description: 'TypeScript isn\'t optional here. Every project is fully typed from the first commit' },
    ],
    relatedFaqIndices: [21, 22, 23, 4, 8, 15],
  },
  {
    id: 'devops',
    slug: 'devops',
    title: 'Managed DevOps',
    accent: 'purple',
    iconName: 'Server',
    summary: 'Infrastructure designed, provisioned and run for you, from DNS and SSL through to CI/CD and servers, so your attention stays on the business.',
    description: 'We provision and run your infrastructure end to end. Servers on DigitalOcean, Hetzner or AWS. Deployments automated through Laravel Forge or a custom CI/CD pipeline. DNS, domains, SSL, firewall rules, monitoring and backups are all on us. What you get is a production environment that\'s secure, quick and actually looked after.',
    whatIs: {
      heading: 'What is Managed DevOps?',
      intro: 'DevOps is the work of keeping the servers, deployments and infrastructure behind your app running. Plenty of agencies build the application and hand you the hosting problem on the way out. We don\'t. We take the full stack, from provisioning the first server through to deployments, monitoring and disaster recovery.',
      points: [
        { label: 'Day one', icon: 'Server', title: 'Servers, hardened', text: 'Production-ready servers on DigitalOcean, Hetzner or AWS, hardened from the first boot.' },
        { label: 'Every push', icon: 'GitMerge', title: 'Tested and deployed', text: 'A push to Git is tested and deployed automatically, with no SSH, no manual uploads and no room for a slip.' },
        { label: 'Day to day', icon: 'Wrench', title: 'Forge does the chores', text: 'Laravel Forge handles the day-to-day server management for us: PHP versions, SSL, queue workers, scheduled tasks.' },
        { label: 'Round the clock', icon: 'Activity', title: 'We hear first', text: 'Monitoring and alerting at the application level, so we hear about a problem before your users do, not just whether the box is up.' },
        { label: 'When it goes wrong', icon: 'DatabaseBackup', title: 'Backups we have restored', text: 'Daily backups that we\'ve actually tested restoring from, rather than set up once and forgotten.' },
        { label: 'The bill', icon: 'Gauge', title: 'Sized to fit', text: 'Servers sized to the traffic you actually get, scaled up before the busy season rather than paid for all year.' },
      ],
    },
    capabilities: [
      'Server provisioning & management',
      'CI/CD pipeline setup (GitHub Actions, Forge)',
      'DNS, domain & SSL management',
      'Database provisioning & replication',
      'Redis, queue workers & schedulers',
      'Firewall & security hardening',
      'Monitoring & alerting',
      'Backup & disaster recovery',
    ],
    tech: ['Laravel Forge', 'DigitalOcean', 'Hetzner', 'AWS'],
    metaTitle: 'Managed DevOps: Servers, CI/CD & Hosting',
    metaDescription: 'Managed DevOps for Laravel applications: server provisioning, CI/CD pipelines, DNS & SSL management, monitoring, and backups. DigitalOcean, Hetzner, and AWS.',
    ogTitle: 'Managed DevOps & Infrastructure | AugmentBLU',
    ogDescription: 'Server provisioning, CI/CD, DNS, SSL, monitoring, and backups, fully managed.',
    logosLabel: 'We deploy to',
    logos: [
      { src: '/images/partners/digitalocean.svg', alt: 'DigitalOcean' },
      { src: '/images/partners/hetzner.svg', alt: 'Hetzner' },
      { src: '/images/partners/forge.svg', alt: 'Laravel Forge' },
      { src: '/images/partners/cloudflare.svg', alt: 'Cloudflare' },
      { src: '/images/partners/letsencrypt.svg', alt: 'Let\'s Encrypt' },
    ],
    approach: [
      { title: 'Provisioning', description: 'Infrastructure goes up before the first line of application code does' },
      { title: 'CI/CD', description: 'Every deploy runs through CI/CD. Nobody is SSHing in to push code by hand' },
      { title: 'Backups', description: 'We test the backups monthly instead of trusting that they work' },
      { title: 'Monitoring', description: 'We watch the application, not just the server\'s CPU graph' },
    ],
    relatedFaqIndices: [24, 25, 26, 10, 8, 7],
  },
  {
    id: 'team',
    slug: 'team-integration',
    title: 'Team Integration',
    accent: 'sky',
    iconName: 'Users',
    summary: 'We slot into the team and tools you already have, working alongside your designers, PMs and stakeholders rather than off to one side.',
    description: 'We don\'t disappear into a corner and resurface weeks later. If you\'ve got designers, a PM or in-house developers, we work inside that setup, in your Slack, your Jira, your Notion. Standups, sprint planning, code review, whatever your week looks like. The aim is to feel like part of the team rather than a contractor you have to keep chasing.',
    whatIs: {
      heading: 'What does team integration look like?',
      intro: 'The usual freelance or agency arrangement keeps everyone at arm\'s length: you write a brief, wait, and hope the thing that comes back is the thing you meant. We work the other way round, embedded in your team and your process, so nothing gets lost between what you need and what actually gets built.',
      points: [
        { label: 'Where we talk', icon: 'MessagesSquare', title: 'In your channels', text: 'We\'re in your Slack channels and your standups, using your project management tools rather than our own.' },
        { label: 'Code review', icon: 'GitPullRequest', title: 'Reviewed both ways', text: 'Your developers read our code, we read theirs, and the standard holds on both sides.' },
        { label: 'Hard problems', icon: 'Users', title: 'Pairing, not hand-offs', text: 'Pair programming on the tricky features and for knowledge transfer, not a finished black box dropped on your desk.' },
        { label: 'Planning', icon: 'CalendarRange', title: 'Estimates in the open', text: 'We estimate and plan in the open, alongside your team, so you watch the work take shape instead of waiting for a reveal.' },
        { label: 'Afterwards', icon: 'BookOpen', title: 'Documented as we go', text: 'Documentation ships with the code: the decisions and the reasoning, not just a few comments in the source.' },
        { label: 'When we step back', icon: 'ArrowRightLeft', title: 'A clean handover', text: 'Your team can run with it the day we leave, because they\'ve been in the work all along.' },
      ],
    },
    capabilities: [
      'Agile / Scrum / Kanban workflows',
      'Sprint planning & estimation',
      'Code reviews & pair programming',
      'Design collaboration (Figma, Sketch)',
      'Project management (Jira, Linear, Notion)',
      'Communication (Slack, Teams)',
      'Documentation & knowledge transfer',
      'Onboarding & handover support',
    ],
    tech: ['Slack', 'Jira', 'Linear', 'Notion', 'Figma', 'GitHub'],
    metaTitle: 'Embedded Laravel Developers for Your Team',
    metaDescription: 'Laravel developers embedded in your team: your standups, your Slack, Jira or Linear, with code reviews and pair programming both ways.',
    ogTitle: 'Team Integration: Embedded Developer | AugmentBLU',
    ogDescription: 'We slot into your team and workflow. Agile, code reviews, and close collaboration.',
    logosLabel: 'We communicate via',
    logos: [
      { src: '/images/partners/slack.svg', alt: 'Slack' },
      { src: '/images/partners/discord.svg', alt: 'Discord' },
    ],
    approach: [
      { title: 'Your Workflow', description: 'We adapt to how you work; you shouldn\'t have to learn ours' },
      { title: 'Code Reviews', description: 'Pull requests get reviewed no matter how small the team is' },
      { title: 'Documentation', description: 'We write down the decisions, not just the code, so your team keeps the context after we\'ve gone' },
      { title: 'Flexibility', description: 'UK business hours as a rule, with room to flex for clients in other time zones' },
    ],
    relatedFaqIndices: [27, 28, 7, 13, 14, 8],
  },
  {
    id: 'ai',
    slug: 'ai-development',
    title: 'AI-Augmented Development',
    accent: 'violet',
    iconName: 'Brain',
    summary: 'We use AI across development for speed, code quality and test coverage, with a human signing off on every decision.',
    description: 'AI is part of how we work day to day, less a gimmick than a serious multiplier on what a senior developer can get done. It helps with code generation, testing, documentation, review and quick prototypes. A human still reads and approves every line. The payoff is faster delivery that doesn\'t quietly cut corners on quality or security.',
    whatIs: {
      heading: 'What is AI-augmented development?',
      intro: 'AI-augmented development means models like Claude and tools like GitHub Copilot are part of the workflow, not as a stand-in for developers but as a way to multiply what a senior one can ship. The operative word is augmented: the AI takes the repetitive work, the people make the calls.',
      points: [
        { label: 'The first draft', icon: 'Sparkles', title: 'AI writes, we review', text: 'AI drafts implementations, migrations and boilerplate, then a senior developer reviews, reshapes and signs it off.' },
        { label: 'The safety net', icon: 'FlaskConical', title: 'Broader test suites', text: 'AI writes broad test suites that catch edge cases it\'s easy to miss by hand, and a person checks they test the right thing.' },
        { label: 'Early on', icon: 'Rocket', title: 'Prototypes in days', text: 'A working prototype in days rather than weeks. Stakeholders see something real early, and we iterate from there.' },
        { label: 'In review', icon: 'ScanEye', title: 'A second pair of eyes', text: 'AI flags bugs, security holes and performance problems during review, a second set of eyes that never gets tired.' },
        { label: 'Proof', icon: 'BadgeCheck', title: 'How we built Inventorai', text: 'We built Inventorai, our own AI SaaS product, exactly this way. It\'s not a theory we\'re selling; it\'s how we work.' },
        { label: 'Always', icon: 'UserCheck', title: 'A person signs off', text: 'Nothing ships on the model\'s word alone: every change has a named developer who understood it and approved it.' },
      ],
    },
    capabilities: [
      'AI-assisted code generation',
      'Automated test writing',
      'Code review assistance',
      'Documentation generation',
      'Rapid prototyping',
      'Bug detection & analysis',
      'Performance optimisation suggestions',
      'Security vulnerability scanning',
    ],
    tech: ['Claude', 'GitHub Copilot', 'Cursor', 'Custom AI tooling'],
    metaTitle: 'AI-Augmented Development, Human Oversight',
    metaDescription: 'AI-augmented Laravel development: AI-assisted code, tests, prototypes and security scanning, with a person signing off every change.',
    ogTitle: 'AI-Augmented Development | AugmentBLU',
    ogDescription: 'AI-powered development for faster delivery, with human oversight on every decision.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Human Oversight', description: 'AI supports the work. It doesn\'t get the final word on architecture or security' },
      { title: 'Code Generation', description: 'We let AI take the first pass, then review and refine every line of it' },
      { title: 'Test Coverage', description: 'Test suites start as AI output and get checked by a person for coverage and edge cases' },
      { title: 'Rapid Prototyping', description: 'Prototypes can land in days rather than weeks, with production-grade code following behind' },
    ],
    relatedFaqIndices: [29, 30, 31, 8, 15],
  },
  {
    id: 'consulting',
    slug: 'consulting',
    title: 'Consulting',
    accent: 'rose',
    iconName: 'Users',
    summary: 'Architecture, scaling and straight technical advice, whether you\'re starting something new or want a second opinion on what you\'ve got.',
    description: 'When you need the expertise but not a full build, we consult. That covers architecture for a new project, a scaling plan for an app that\'s growing fast, technical due diligence ahead of an acquisition, or simply a second opinion before a big decision. Behind it is 24+ years of Laravel and infrastructure work.',
    whatIs: {
      heading: 'What does consulting look like?',
      intro: 'Consulting is the expertise without the full build. Maybe you want an architecture review before you start, a sanity check on a scaling decision, or due diligence on a codebase you\'re about to acquire. We bring 24+ years to the conversation and give you honest, specific recommendations you can act on.',
      points: [
        { label: 'Your code', icon: 'FileSearch', title: 'Architecture reviews', text: 'We go through your codebase or proposed design and hand back a written report with specific, prioritised recommendations.' },
        { label: 'Growth', icon: 'TrendingUp', title: 'Scaling strategy', text: 'When an app is growing, you need to know what gives way first. We find the bottlenecks and map what to fix, in order.' },
        { label: 'Before you buy', icon: 'Scale', title: 'Due diligence', text: 'Assessing a codebase before an acquisition or investment, written for the business stakeholders as well as the engineers.' },
        { label: 'Choosing', icon: 'ListChecks', title: 'Technology selection', text: 'Choosing between frameworks, platforms or hosts on the basis of your team and budget rather than fashion.' },
        { label: 'Security', icon: 'ShieldAlert', title: 'Security reviews', text: 'A look at how the app handles logins, data and dependencies, with the risks ranked by what they could cost you.' },
        { label: 'A second opinion', icon: 'MessageCircleQuestion', title: 'Straight answers', text: 'An hour or a retainer: a senior developer to ask before a decision is made, rather than after it goes wrong.' },
      ],
    },
    capabilities: [
      'Architecture design & review',
      'Scaling strategy & planning',
      'Technical due diligence',
      'Technology selection guidance',
      'Migration planning',
      'Performance strategy',
      'Security recommendations',
    ],
    tech: ['Laravel', 'Architecture', 'Strategy', 'Mentoring'],
    metaTitle: 'Laravel Consulting & Technical Due Diligence',
    metaDescription: 'Laravel consulting services: architecture design, scaling strategy, technical due diligence. 24+ years experience. Edinburgh-based.',
    ogTitle: 'Laravel Consulting & Architecture Review | AugmentBLU',
    ogDescription: 'Architecture design, scaling strategy, and technical guidance from 24+ years of experience.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Honest Advice', description: 'We tell you what we actually think, even when the honest answer is less work for us' },
      { title: 'Actionable Reviews', description: 'Architecture reviews come back with things you can do, not vague hand-waving' },
      { title: 'Due Diligence', description: 'Due diligence reports are written for business stakeholders, not just developers' },
      { title: 'Knowledge Transfer', description: 'We\'ll pair with your team to pass on what we know, rather than just handing over a document' },
    ],
    relatedFaqIndices: [32, 33, 5, 11, 15, 7],
  },
  {
    id: 'api',
    slug: 'api-development',
    title: 'API Development',
    accent: 'blu',
    iconName: 'Globe',
    summary: 'REST and GraphQL APIs that connect your systems, your apps and the third-party services you depend on.',
    description: 'We build APIs that other software can actually rely on: versioned, documented and resilient when a request fails. REST or GraphQL, with token auth through Sanctum or Passport, rate limiting, and webhook handling for the integrations that need it. Whether you\'re connecting a mobile app, a partner system or a fleet of services, this is the layer that holds it together.',
    whatIs: {
      heading: 'What goes into a good API?',
      intro: 'An API is the contract between your application and everything that talks to it: mobile apps, partner systems, your own frontend. A good one is predictable, properly versioned and forgiving when something on the other end misbehaves. We build APIs the way we\'d want to consume them, with clear documentation and sensible error responses rather than a black box.',
      points: [
        { label: 'The shape', icon: 'Braces', title: 'REST or GraphQL', text: 'REST or GraphQL, chosen to fit how the API will actually be used.' },
        { label: 'Who gets in', icon: 'KeyRound', title: 'Scoped tokens', text: 'Token authentication with Sanctum or Passport, scoped so each client only sees what it should.' },
        { label: 'Change', icon: 'GitBranch', title: 'Versioned from day one', text: 'Versioning from the start, so you can change the API without breaking the apps already using it.' },
        { label: 'The unhappy path', icon: 'ShieldCheck', title: 'Built for misuse', text: 'Rate limiting, request validation and consistent error responses, not just the happy path.' },
        { label: 'For developers', icon: 'BookOpen', title: 'Docs that keep up', text: 'Generated documentation with Scribe or Spotlight, kept in step with the code.' },
        { label: 'Other systems', icon: 'Webhook', title: 'Webhooks and integrations', text: 'Webhooks out and third-party APIs in, with retries and logging so nothing goes missing quietly.' },
      ],
    },
    capabilities: [
      'REST & GraphQL endpoints',
      'OAuth & token authentication',
      'API versioning & deprecation',
      'Rate limiting & throttling',
      'Webhook delivery & processing',
      'Third-party API integrations',
      'Auto-generated API documentation',
      'Mobile & SPA backends',
    ],
    tech: ['Laravel', 'Sanctum', 'Passport', 'GraphQL', 'Scribe'],
    metaTitle: 'Laravel API Development: REST & GraphQL',
    metaDescription: 'Laravel API development: REST and GraphQL, token auth, versioning, rate limiting, webhooks and generated docs. Edinburgh-based, UK-wide.',
    ogTitle: 'Laravel API Development | AugmentBLU',
    ogDescription: 'REST and GraphQL APIs built on Laravel, with auth, versioning, and proper documentation.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Contract First', description: 'We agree the shape of the API up front, so the teams consuming it can build in parallel' },
      { title: 'Versioned', description: 'Every API is versioned from day one, so later changes never break existing clients' },
      { title: 'Documented', description: 'Documentation is generated from the code and stays accurate, rather than rotting in a wiki' },
      { title: 'Resilient', description: 'We design for the failure cases: retries, idempotency and clear errors, not just the happy path' },
    ],
    relatedFaqIndices: [1, 6, 0, 8, 15],
  },
  {
    id: 'testing',
    slug: 'testing-qa',
    title: 'Testing & QA',
    accent: 'green',
    iconName: 'ShieldCheck',
    summary: 'Automated test suites and quality checks, so you can ship changes without holding your breath.',
    description: 'Tests are how we ship quickly without breaking things. We write them in PHPUnit and Pest for the backend, use Dusk for the browser flows that matter, and run the whole suite on every push through CI. The point isn\'t a coverage number to brag about; it\'s that a deploy on a Friday afternoon doesn\'t keep anyone up that night.',
    whatIs: {
      heading: 'Why does testing matter?',
      intro: 'A test suite is what lets a team change code with confidence. Without one, every release is a gamble and every refactor is a risk. With one, the machine checks the boring cases so the developers can spend their attention on the interesting ones. We treat tests as part of the work, not an optional extra bolted on at the end.',
      points: [
        { label: 'The logic', icon: 'FlaskConical', title: 'Unit and feature tests', text: 'Unit and feature tests in PHPUnit and Pest, covering the logic that actually matters.' },
        { label: 'In the browser', icon: 'MousePointerClick', title: 'Browser tests', text: 'Browser tests with Laravel Dusk for the flows where a broken button costs you money.' },
        { label: 'Every push', icon: 'GitMerge', title: 'Runs in CI', text: 'The suite runs on every push through CI, so nothing merges that quietly breaks something else.' },
        { label: 'The odd cases', icon: 'Bug', title: 'Edge cases kept', text: 'We cover the edge cases a quick manual check would skip, then keep them as a safety net.' },
        { label: 'Before it runs', icon: 'ScanSearch', title: 'Static analysis', text: 'Static analysis with PHPStan and Larastan to catch the bugs tests alone would miss.' },
        { label: 'Under load', icon: 'Gauge', title: 'Performance checks', text: 'Load tests on the pages that matter, so you know how the app behaves on your busiest day before it arrives.' },
      ],
    },
    capabilities: [
      'PHPUnit & Pest test suites',
      'Browser testing with Laravel Dusk',
      'CI test pipelines',
      'Test coverage analysis',
      'Regression test suites',
      'Static analysis (PHPStan, Larastan)',
      'Code review & standards',
      'Legacy code test harnesses',
    ],
    tech: ['PHPUnit', 'Pest', 'Laravel Dusk', 'PHPStan', 'GitHub Actions'],
    metaTitle: 'Laravel Testing & QA: Pest, PHPUnit & Dusk',
    metaDescription: 'Laravel testing and QA: Pest and PHPUnit suites, Dusk browser tests, CI pipelines and static analysis, so changes ship with confidence.',
    ogTitle: 'Laravel Testing & QA | AugmentBLU',
    ogDescription: 'Automated test suites and quality assurance for Laravel: PHPUnit, Pest, Dusk, and CI.',
    logosLabel: '',
    logos: [],
    approach: [
      { title: 'Tests as Delivery', description: 'Tests ship with the feature, not as a phase we get to later, or never' },
      { title: 'The Right Coverage', description: 'We test what carries risk rather than chasing a coverage percentage for its own sake' },
      { title: 'Automated in CI', description: 'The full suite runs on every push, so a broken change is caught before it can merge' },
      { title: 'Human Review Too', description: 'Automated checks catch the mechanical problems; a person still reads every pull request' },
    ],
    relatedFaqIndices: [11, 5, 8, 15, 1],
  },
]

// Text in each accent: the 700 shade on the light theme and the 400 on the
// dark, so it passes WCAG AA contrast on both.
export const accentClasses: Record<string, { bar: string; icon: string; tag: string }> = {
  blu: { bar: 'bg-blu-400', icon: 'text-blu-600 dark:text-blu-400', tag: 'bg-blu-500/10 text-blu-600 dark:text-blu-400 border-blu-500/20' },
  red: { bar: 'bg-red-500', icon: 'text-red-700 dark:text-red-400', tag: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20' },
  amber: { bar: 'bg-amber-400', icon: 'text-amber-700 dark:text-amber-400', tag: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' },
  teal: { bar: 'bg-teal-400', icon: 'text-teal-700 dark:text-teal-400', tag: 'bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20' },
  green: { bar: 'bg-green-400', icon: 'text-green-700 dark:text-green-400', tag: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20' },
  purple: { bar: 'bg-purple-400', icon: 'text-purple-700 dark:text-purple-400', tag: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20' },
  sky: { bar: 'bg-sky-400', icon: 'text-sky-700 dark:text-sky-400', tag: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20' },
  violet: { bar: 'bg-violet-400', icon: 'text-violet-700 dark:text-violet-400', tag: 'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20' },
  rose: { bar: 'bg-rose-400', icon: 'text-rose-700 dark:text-rose-400', tag: 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20' },
}

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return services.find((s) => s.slug === slug)
}
